import classNames from "classnames";
import { Formik } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";

import style from "./generate-api-key.scss";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import { TextBlock } from "components/typography/textBlock/TextBlock";
import { ApiKeyScope } from "domain/apiKeys";
import { NAME_MAX_LENGTH, NOTES_MAX_LENGTH } from "domain/globalConstants";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import buttons from "styles/buttons.scss";
import form from "styles/form.scss";
import { formatIsoDate } from "utils/format";

import testIds from "testIds.json";

interface Props {
    submitEventHandler: (values: FormValues) => Promise<void>;
    closeHandler: () => void;
}

export interface FormValues {
    name: string;
    description: string;
    expiration: string;
    scope: ApiKeyScope;
}

export default function GenerateApiKeyForm(props: Props): JSX.Element {
    const { t } = useTranslation();
    const minDate = new Date().toISOString().split("T")[0];

    const oneYearFromToday = new Date();
    oneYearFromToday.setFullYear(oneYearFromToday.getFullYear() + 1);

    const [loading, setLoading] = React.useState(false);
    const [descriptionCharactersLeft, setDescriptionCharactersLeft] = React.useState(NOTES_MAX_LENGTH);
    const [disabled, setDisabled] = React.useState(true);

    const initialValues: FormValues = {
        name: "",
        description: "",
        expiration: formatIsoDate(oneYearFromToday),
        scope: "ALL",
    };

    const nameChangeHandler = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setDisabled(!event.target.value.replace(/\s/g, "").length);
    };

    const descriptionChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
        setDescriptionCharactersLeft(event.target.maxLength - event.target.value.length);
    };

    if (loading) {
        return (
            <div className={style.loadingIndicator}>
                <LoadingIndicator />
            </div>
        );
    }

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={async (values: FormValues) => {
                setLoading(true);
                try {
                    await props.submitEventHandler(values);
                } finally {
                    setLoading(false);
                }
            }}
        >
            {(formikProps) => (
                <form onSubmit={formikProps.handleSubmit}>
                    <TextBlock>{t("GenerateApiKeyForm.generateApiKeySummaryText")}</TextBlock>
                    <div className={form.formFields}>
                        <label htmlFor="name" className={form.label}>
                            {t("Common.nameWithColon")}
                        </label>
                        <input
                            id="name"
                            className={classNames(form.input, style.inputWidth)}
                            maxLength={NAME_MAX_LENGTH}
                            autoFocus
                            name="name"
                            onChange={(e) => {
                                formikProps.handleChange(e);
                                nameChangeHandler(e);
                            }}
                            onBlur={formikProps.handleBlur}
                            value={formikProps.values.name}
                            data-testid={testIds.workArea.support.apiKeys.manageApiKeyDialog.nameInput.itself}
                        />
                    </div>

                    <div className={form.formFields}>
                        <label htmlFor="scope" className={form.label}>
                            {t("ApiKeys.scopeWithColon")}
                        </label>
                        <select
                            id="scope"
                            className={classNames(form.select, style.inputWidth)}
                            name="scope"
                            value={formikProps.values.scope}
                            data-testid={testIds.workArea.support.apiKeys.manageApiKeyDialog.scopeSelect}
                            onChange={(e) => {
                                formikProps.handleChange(e);
                                usageStatisticsService.sendEvent({
                                    category: Category.API_KEYS,
                                    action: Action.CHANGE_API_KEY_SCOPE,
                                    label: e.currentTarget.value,
                                });
                            }}
                        >
                            <option value="ALL">{t("GenerateApiKeyForm.scopes.all")}</option>
                            <option value="ERASURE_CLIENT">{t("GenerateApiKeyForm.scopes.erasureClient")}</option>
                            <option value="PUBLIC_API">{t("GenerateApiKeyForm.scopes.publicApi")}</option>
                            <option value="MCP">{t("GenerateApiKeyForm.scopes.mcp")}</option>
                        </select>
                    </div>

                    <div className={form.formFields}>
                        <div className={form.formFieldsColumnDirection}>
                            <span className={form.optional}>{t("Common.optional")}</span>
                            <label htmlFor="expiration" className={form.label}>
                                {t("Common.expirationDateWithColon")}
                            </label>
                        </div>
                        <input
                            id="expiration"
                            type="date"
                            className={classNames(form.input, style.inputWidth)}
                            min={minDate}
                            name="expiration"
                            onChange={(e) => {
                                formikProps.handleChange(e);
                                if (e.target.value === "") {
                                    usageStatisticsService.sendEvent({
                                        category: Category.API_KEYS,
                                        action: Action.REMOVE_API_KEY_EXPIRATION,
                                    });
                                } else {
                                    usageStatisticsService.sendEvent({
                                        category: Category.API_KEYS,
                                        action: Action.CHANGE_API_KEY_EXPIRATION,
                                    });
                                }
                            }}
                            onBlur={formikProps.handleBlur}
                            value={formikProps.values.expiration}
                            data-testid={testIds.workArea.support.apiKeys.manageApiKeyDialog.expirationDateInput}
                        />
                    </div>

                    <div className={form.formFields}>
                        <div className={form.formFieldsColumnDirection}>
                            <span className={form.optional}>{t("Common.optional")}</span>
                            <label htmlFor="description" className={form.label}>
                                {t("ApiKeys.descriptionWithColon")}
                            </label>
                        </div>
                        <div className={style.descriptionContainer}>
                            <textarea
                                id="description"
                                className={classNames(form.input, style.inputWidth, style.descriptionHeight)}
                                maxLength={NOTES_MAX_LENGTH}
                                onChange={(e) => {
                                    descriptionChangeHandler(e);
                                    formikProps.handleChange(e);
                                }}
                                data-testid={
                                    testIds.workArea.support.apiKeys.manageApiKeyDialog.descriptionInput.itself
                                }
                            />
                            <span className={form.optional}>
                                {t("GenerateApiKeyForm.charactersLeft", {
                                    remainingCharacters: descriptionCharactersLeft.toString(),
                                    descriptionMaxLength: NOTES_MAX_LENGTH.toString(),
                                })}
                            </span>
                        </div>
                    </div>
                    <div className={form.buttonContainer}>
                        <button
                            type="submit"
                            disabled={disabled}
                            className={buttons.primaryButtonWithoutIcon}
                            data-testid={testIds.workArea.support.apiKeys.manageApiKeyDialog.generateButton}
                        >
                            {t("GenerateApiKeyForm.generateButton")}
                        </button>
                    </div>
                </form>
            )}
        </Formik>
    );
}
